import React, { useState, useEffect, useCallback } from "react";
import { StyledFileRow } from "./StyleComponents";
import { EditFormElement } from "@kaltura/mediaspace-shared-edit-entry";
import { UploadStatus, MediaUploadFileInfo, StatusesMapper } from "@kaltura/mediaspace-shared-upload";
import { KalturaMediaEntry } from "kaltura-typescript-client/api/types/KalturaMediaEntry";
import { deleteEntry, checkAddFromUrlEntryStatus } from "../services/addFromUrlService";
import { KalturaEntryStatus } from "kaltura-typescript-client/api/types/KalturaEntryStatus";
import { ConfirmationModal } from "@kaltura/mediaspace-shared-ui";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useInterval } from "@kaltura/mediaspace-shared-hooks";

interface AddFromUrlEditFormProps {
    entry: KalturaMediaEntry;
    fieldsStructure: EditFormElement[];
    editRowKey: number;
    onCancel: () => void;
    onEdit: () => void;
    onBeforeSubmit?: (entryIds: string[], data: any) => Promise<{ confirm: boolean; isEditable: boolean }>;
    onSubmit: (data: any) => void;
    onSubmitStarted: () => void;
    onRemove: () => void;
    onClose: () => void;
    publishEnabled?: boolean;
    formId: string;
    showEditFormButtons?: boolean;
    onCanSaveForm: (canSave: boolean) => void;
    onReadyForPublish?: (entryIds: string[], readyForPublish: boolean) => void;
    showMediaLink?: boolean;
}

const POLLING_INTERVAL_MS = 7000;

/**
 * AddFromUrlEditForm - Displays and manages an entry imported from a URL
 *
 * Features:
 * - Polls backend every 7 seconds to check entry processing status
 * - Updates UI based on entry status (importing, ready, failed, etc.)
 * - Allows editing entry metadata through EditForm integration
 * - Handles cancellation and cleanup of entries
 *
 * Status Flow:
 * 1. Entry created → Polls for status updates
 * 2. Entry processing → Shows progress
 * 3. Entry ready/failed → Stops polling, shows final state
 */
const AddFromUrlEditForm: React.FC<AddFromUrlEditFormProps> = ({
    entry,
    fieldsStructure,
    editRowKey,
    onCancel,
    onEdit,
    onBeforeSubmit,
    onSubmit,
    onSubmitStarted,
    onRemove,
    onClose,
    onReadyForPublish,
    publishEnabled = true,
    formId,
    showEditFormButtons = false,
    onCanSaveForm,
    showMediaLink = true,
}) => {
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [shouldPoll, setShouldPoll] = useState<boolean>(!!entry.id);
    const [fileInfo, setFileInfo] = useState<MediaUploadFileInfo>(() => {
        const kalturaStatus = entry.status as KalturaEntryStatus;
        const statusKey = StatusesMapper[kalturaStatus] ?? "READY";
        const dynamicStatus = UploadStatus[statusKey];

        return {
            entry: entry as KalturaMediaEntry,
            entryMetadata: {},
            fileMetaData: {
                progress: dynamicStatus === UploadStatus.READY ? 100 : 0,
                status: dynamicStatus,
                tokenId: "",
            },
        } as MediaUploadFileInfo;
    });

    // Checks the latest status for the entry and updates local state.
    // Used by both polling interval and initial mount.
    // Stops polling when entry reaches terminal state (READY, FAILED, CANCELED).
    const checkStatusOnce = useCallback(async () => {
        if (!entry.id) return;

        try {
            const result = await checkAddFromUrlEntryStatus(entry.id);

            if (!result) {
                return;
            }

            const { entry: updatedEntry, uploadStatus } = result;

            setFileInfo((current) => ({
                ...current,
                entry: updatedEntry,
                fileMetaData: {
                    ...current.fileMetaData,
                    status: uploadStatus,
                    progress: uploadStatus === UploadStatus.READY ? 100 : current.fileMetaData.progress,
                },
            }));

            // Stop polling when entry reaches terminal state
            if (
                [
                    UploadStatus.READY,
                    UploadStatus.ENTRY_FAILED,
                    UploadStatus.UPLOAD_FAILED,
                    UploadStatus.CANCELED,
                ].includes(uploadStatus)
            ) {
                setShouldPoll(false);
            }
        }
        catch (error) {
            console.error("Error checking entry status", error);
        }
    }, [entry.id]);

    // Sync polling state whenever the current entry changes
    useEffect(() => {
        setShouldPoll(!!entry.id);
    }, [entry.id]);

    // Check status immediately on mount or when entry ID changes
    useEffect(() => {
        void checkStatusOnce();
    }, [checkStatusOnce]);

    // Poll for status updates every 7 seconds while shouldPoll is true
    useInterval(
        () => {
            void checkStatusOnce();
        },
        shouldPoll ? POLLING_INTERVAL_MS : null
    );

    const handleFileRowCancel = (_uploadTokenId: string) => {
        setShowCancelModal(true);
    };

    const handleCancelBack = () => {
        setShowCancelModal(false);
    };

    const handleCancelConfirm = async () => {
        setShowCancelModal(false);

        if (entry.id) {
            try {
                await deleteEntry(entry.id);
            }
            catch (error) {
                console.error("Failed to delete entry:", error);
            }
        }

        onRemove();
    };

    return (
        <>
            <StyledFileRow
                fileInfo={fileInfo}
                isOpen={!!fileInfo.entry}
                shouldClose={false}
                editForm={fieldsStructure}
                onCancel={handleFileRowCancel}
                onEdit={onEdit}
                onBeforeSubmit={onBeforeSubmit}
                onSubmit={onSubmit}
                onSubmitStarted={onSubmitStarted}
                onRemove={onRemove}
                onClose={onClose}
                key={editRowKey}
                publishEnabled={publishEnabled}
                formId={formId}
                showEditFormButtons={showEditFormButtons}
                onCanSaveForm={onCanSaveForm}
                onReadyForPublish={onReadyForPublish}
                singleUpload={true}
                showMediaLink={showMediaLink}
            />
            {showCancelModal && (
                <ConfirmationModal
                    open={showCancelModal}
                    title={translate("Cancel Upload")}
                    text={
                        `${translate("Are you sure you want to cancel this upload?")}<br/>`
                        + translate("This action cannot be undone.")
                    }
                    noCallback={handleCancelBack}
                    yesCallback={handleCancelConfirm}
                    noButtonLabel={translate("Back")}
                    yesButtonLabel={translate("Cancel upload")}
                />
            )}
        </>
    );
};

export default AddFromUrlEditForm;
